import { memo, type Ref } from "react";

import CartProductTableHeaderCell from "./CartProductTableHeaderCell";
import classNames from "classnames";
import ClearCartButton from "./ClearCartButton";
import ProductEntryList, { type ProductEntryListRef } from "./ProductEntryList/ProductEntryList";
import { HAS_END_CUSTOMER } from "../../../../../../utils/constants";
import { Permission, userHasPermission } from "../../../../../../utils/permissions";
import { type CartGrouping } from "~/feature/cart/cart/models/CartData";
import { userCanSeePriceReference } from "../LineItemStaticParams";
import { useTranslation } from "react-i18next";
import AlternateProducts from "../AlternateProducts";
import { useAlternateProducts } from "~/feature/cart/cart/AlternateProductsContext";

interface ProductTableProps {
  className?: string;
  group: CartGrouping;
  editMode?: boolean;
  onClear?: () => void;
  "data-test-id"?: string;
  scrollRef?: Ref<ProductEntryListRef>;
}

const ProductTable = (props: ProductTableProps) => {
  const { group, editMode, "data-test-id": testId } = props;
  const { alternateItems, handleClose, handleClear } = useAlternateProducts();

  const { t } = useTranslation();
  const cls = classNames(props.className, "scha__cart__product-table scha__data-table");

  const headerTitle = (header: string) => t(`cartPage.table.header.${header}.title`);
  const headerSubtitle = (header: string) => t(`cartPage.table.header.${header}.subtitle`);

  const isEmpty = group.entries.length === 0;
  const userHasEndCustomerPermission = userHasPermission(Permission.SEE_ENDCUSTOMER_TABULAR_VIEW);

  const isCheckoutPage = !editMode;

  const showPriceRefColumn = userCanSeePriceReference() && isCheckoutPage;

  const clearCart = () => {
    if (props.onClear) {
      props.onClear();
      handleClear();
    }
  };

  return (
    <>
      <div className={cls} data-test-id={testId}>
        <div className="scha__cart__product-table__header">
          <CartProductTableHeaderCell title={headerTitle("product")} first={true} />
          <CartProductTableHeaderCell title={headerTitle("quantity")} firstOnRightSide={true} />
          <CartProductTableHeaderCell title={headerTitle("packaging")} subTitle={headerSubtitle("packaging")} />
          <CartProductTableHeaderCell
            title={headerTitle("delivery")}
            subTitle={window.SCHA.config.showESD ? headerSubtitle("delivery") : headerSubtitle("arrival")}
          />
          <CartProductTableHeaderCell title={headerTitle("availability")} />
          {userHasEndCustomerPermission && isCheckoutPage
            ? HAS_END_CUSTOMER && <CartProductTableHeaderCell title={headerTitle("endCustomer")} />
            : showPriceRefColumn && <CartProductTableHeaderCell title={headerTitle("priceReference")} />}
          <CartProductTableHeaderCell title={headerTitle("price")} subTitle={headerSubtitle("price")} />
          <CartProductTableHeaderCell title={headerTitle("total")} />
          {!showPriceRefColumn && (
            <CartProductTableHeaderCell
              lastOnRightSide={true}
              alignEnd={true}
              title={
                props.onClear && (
                  <ClearCartButton onClick={clearCart} disabled={isEmpty} data-test-id={"btn-clear-section"} />
                )
              }
            />
          )}
        </div>
        <div className="scha__cart__product-table__body">
          {isEmpty &&
            alternateItems.map((item) => (
              <AlternateProducts
                key={item.index}
                orderEntryNumber={item.index}
                alternateProductsId={item.code}
                alternativesFor={item.designation}
                onClose={() => handleClose(item.index)}
              />
            ))}
          {!isEmpty && <ProductEntryList group={group} editMode={editMode} scrollRef={props.scrollRef} />}
        </div>
      </div>
    </>
  );
};

export default memo(ProductTable);
